import { DocumentPickerContainer } from '@/features/documents/DocumentPicker/DocumentPickerContainer'
import { observer } from 'mobx-react-lite'
import { SideSheetHostView, useSideSheet } from '@/components/SideSheet'
import { type JobDetailsViewModel } from '@/features/jobs/details/JobDetailsViewModel'
import { HeaderViewContainer as ClassicHeaderViewContainer } from '@/features/jobs/details/components/HeaderView/HeaderViewContainer'
import { ContentViewContainer as ClassicContentViewContainer } from '@/features/jobs/details/components/ContentViewContainer'
import { JobTopBarContainer } from '@/features/jobs/details/components/JobTopBar/JobTopBarContainer'
import { JobContentContainer } from '@/features/jobs/details/components/JobContent/JobContentContainer'
import { ChatPopoverContainer } from '@/features/jobs/details/components/ChatPopover/ChatPopoverContainer'
import { useDropzone } from '@/hooks/dropzone-hook'
import { JobCards } from '@/features/jobs/details/components/JobCards'
import { PtinRequiredView } from '@/features/iam/providers/components/PtinRequiredView'
import { isRefreshDesign } from '@/infra/design'
import { useFeatures } from '@/contexts/root-store-context'
import { useActivation } from '@/hooks/view-model-hooks'
import { JobDetailsDialogsHostContainer } from '@/features/jobs/details/components/JobDetailsDialogsHostContainer'

const ContentViewContainer = isRefreshDesign
  ? JobContentContainer
  : ClassicContentViewContainer

export interface JobCardsContainerProps {
  vm: JobDetailsViewModel
  onJobListSidebarToggle?: () => unknown
  onJobListToggleHoverChange: (hovering: boolean) => unknown
  isJobListSidebarVisible: boolean
  onPreviousJob?: () => unknown
  onNextJob?: () => unknown
}

export const JobCardsContainer = observer(function JobCardsContainer({
  vm,
  onJobListSidebarToggle,
  onJobListToggleHoverChange,
  isJobListSidebarVisible,
  onPreviousJob,
  onNextJob,
}: JobCardsContainerProps) {
  const { shellInfo } = useFeatures(({ shell }) => ({
    shellInfo: shell.shellInfo,
  }))
  const sideSheet = useSideSheet()
  useActivation(vm)

  const { dropzoneProps } = useDropzone<HTMLDivElement>({
    onFilesDropped: vm.documentsVm.dropFiles,
    onEnter: vm.documentsVm.uploadBarVm.dragEnter,
    onLeave: vm.documentsVm.uploadBarVm.dragLeave,
  })

  if (vm.ptinRequired) {
    return <PtinRequiredView corporateMode={shellInfo.corporateMode} />
  }

  return (
    <SideSheetHostView
      elevation={sideSheet.elevation}
      onRequestHide={sideSheet.hide}
      sheetVisible={sideSheet.visible || !sideSheet.shouldBeAbove}
    >
      <JobCards
        dropzoneProps={vm.job.status !== 'Closed' ? dropzoneProps : undefined}
      >
        {isRefreshDesign ? (
          <JobTopBarContainer
            vm={vm}
            onJobListHoverChange={onJobListToggleHoverChange}
            onJobListSidebarToggle={onJobListSidebarToggle}
            isJobListSidebarVisible={isJobListSidebarVisible}
            onPreviousJob={onPreviousJob}
            onNextJob={onNextJob}
          />
        ) : (
          <ClassicHeaderViewContainer
            vm={vm}
            onJobListHoverChange={onJobListToggleHoverChange}
            onJobListSidebarToggle={onJobListSidebarToggle}
            isJobListSidebarVisible={isJobListSidebarVisible}
          />
        )}
        <ContentViewContainer vm={vm} />
        {isRefreshDesign && <ChatPopoverContainer vm={vm} />}
        <DocumentPickerContainer
          searchPlaceholder={`Select documents to add to ${vm.job.name}`}
          vm={vm.documentLinkingPickerVm}
        />
      </JobCards>
      <JobDetailsDialogsHostContainer vm={vm} />
    </SideSheetHostView>
  )
})
